import React, { useState, useMemo } from 'react';
import { Link2, Eye, Settings } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';

import { queryClient } from '@api/queryClient';
import { EventType } from 'types/event.types';
import { cn } from '@epoch-ui/lib/utils';
import { Button } from '@epoch-ui/components/Button';
import { Question } from 'types/question.types';
import { SurveyTemplateTypes } from 'types/survey_template.types';
import dayjs from 'utils/extendedDayJS';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { DraggableQuestionsList } from '@event-editor/components/DraggableQuestionsList';
import { EventEditorEmptyQuestions } from '@event-editor/components/EventEditorEmptyQuestions';
import { TemplateSelectionModal } from '@event-editor/components/TemplateSelectionModal/TemplateSelectionModal';
import { reorderEventFeedbackQuestions } from '@event-editor/api/feedback/reorderEventFeedbackQuestions';
import { useEditFeedbackSettingsModal } from '@event-editor/hooks/eventEditorState';
import { useSessionsQuery } from '@event-editor/hooks/useSessionsQuery';

import { FeedbackPreviewModal } from '../components/FeedbackPreviewModal';
import { EditFeedbackQuestionSideSheet } from '../components/EditFeedbackQuestionSideSheet/EditFeedbackQuestionSideSheet';
import { FeedbackWarningBanner, FeedbackWarningType } from '../components/FeedbackWarningBanner';
import { PastEventEmptyFeedback } from '../components/PastEventEmptyFeedback';
import { EmptyMultiSessionEventFeedback } from '../components/EmptyMultiSessionEventFeedback';

export const FeedbackFormQuestionsSection = (): React.ReactElement => {
  const event = useEditableEvent();
  const sessionsQuery = useSessionsQuery(event);
  const { showToast } = useToaster();
  const editFeedbackSettingsModal = useEditFeedbackSettingsModal();

  const isCancelled = !!event.scheduledDeletionAt;

  const disabled = useMemo(() => {
    if (!event.datetimeEnd) {
      return false;
    }

    // Return true if 30 days have passed since the event ended
    return (
      isCancelled ||
      (event.hasEnded && dayjs.utc().isAfter(dayjs.utc(event.datetimeEnd).add(30, 'days')))
    );
  }, [event.datetimeEnd, isCancelled, event.hasEnded]);

  const sendAtHasPassed = useMemo(() => {
    return (
      event.feedbackSchedule?.sendAt &&
      dayjs.utc().isAfter(dayjs.utc(event.feedbackSchedule.sendAt))
    );
  }, [event.feedbackSchedule?.sendAt]);

  const questions = useMemo(
    () =>
      event.postQuestions
        ?.filter((q) => !q.isDeleted)
        .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0)) || [],
    [event.postQuestions]
  );

  const warningUpdateSettings = useMemo(
    () => questions.length > 0 && !event.feedbackSchedule?.sendAt,
    [questions.length, event.feedbackSchedule?.sendAt]
  );

  const isEmptyMultiSession = useMemo(() => {
    return event.eventType === EventType.MULTI_SESSIONS && sessionsQuery.data?.length === 0;
  }, [sessionsQuery.data, event.eventType]);

  const [selectedQuestion, setSelectedQuestion] = useState<Question | undefined>(undefined);
  const [warningResendFeedback, setWarningResendFeedback] = useState<boolean>(false);

  const [isTemplateSelectionModalOpen, setIsTemplateSelectionModalOpen] = useState(false);
  const [isQuestionPreviewModalOpen, setIsQuestionPreviewModalOpen] = useState(false);
  const [isEditQuestionSideSheetOpen, setIsEditQuestionSideSheetOpen] = useState(false);

  const reorderQuestionsMutation = useMutation({
    mutationFn: (variables: { eventId: number; questions: Question[] }) =>
      reorderEventFeedbackQuestions({
        eventId: variables.eventId,
        questionSortOrders: variables.questions.map((question, index) => ({
          questionId: question.id!,
          sortOrder: index
        }))
      }),
    onMutate: async (variables) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({
        queryKey: eventEditorQueryKeys.event(variables.eventId)
      });

      // Snapshot the previous value
      const previousEvent = queryClient.getQueryData(eventEditorQueryKeys.event(variables.eventId));

      // Optimistically update the cache with the new questions
      queryClient.setQueryData(eventEditorQueryKeys.event(variables.eventId), (oldData: any) => {
        if (!oldData?.event.postQuestions) return oldData;

        return {
          event: {
            ...oldData.event,
            postQuestions: oldData.event.postQuestions.map(
              (existingQuestion: Question) =>
                variables.questions.find((q) => q.id === existingQuestion.id) || existingQuestion
            )
          }
        };
      });

      // Return a context object with the snapshotted value
      return { previousEvent };
    },
    onError: (err, variables, context) => {
      // If the mutation fails, roll back to the previous value
      if (context?.previousEvent) {
        queryClient.setQueryData(
          eventEditorQueryKeys.event(variables.eventId),
          context.previousEvent
        );
      }
      showToast('Failed to reorder questions', { variant: ToasterVariant.ERROR });
    }
  });

  return (
    <div>
      <div className="tw-mb-4">
        <div className="tw-flex tw-items-center tw-justify-between">
          <h2
            className={cn(
              'tw-m-0 tw-mb-1 tw-text-xl tw-font-medium tw-leading-7',
              disabled && 'tw-opacity-40'
            )}
          >
            Feedback form questions
          </h2>

          {/* Action buttons in header - only show when questions exist */}
          {questions.length > 0 && !disabled && !isEmptyMultiSession && (
            <div className="tw-flex tw-items-center tw-gap-2">
              {/* Copy button */}
              <Button
                size="xxs"
                className="tw-gap-1 tw-rounded-xl tw-leading-4"
                variant="secondary"
                onClick={() => {
                  if (!event.id) return;
                  const url = new URL(`/event/${event.id}`, window.location.origin);
                  url.searchParams.set('feedbackModal', 'true');
                  navigator.clipboard
                    .writeText(url.toString())
                    .then(() => {
                      showToast('Link copied');
                    })
                    .catch(() => {
                      showToast('Failed to copy link', { variant: ToasterVariant.ERROR });
                    });
                }}
              >
                <Link2 className="tw-text-black" size={16} />
                Copy
              </Button>

              {/* Preview button */}
              <Button
                size="xxs"
                className="tw-gap-1 tw-rounded-xl tw-leading-4"
                variant="secondary"
                onClick={() => setIsQuestionPreviewModalOpen(true)}
              >
                <Eye className="tw-text-black" size={16} />
                Preview
              </Button>

              {/* Settings button */}
              <Button
                size="xxs"
                className="tw-gap-1 tw-rounded-xl tw-leading-4"
                variant="secondary"
                onClick={() => editFeedbackSettingsModal.open()}
              >
                <Settings className="tw-text-black" size={16} />
                Settings
              </Button>
            </div>
          )}
        </div>

        {/* Warning banner - show when feedback form has been sent */}
        {(warningUpdateSettings || warningResendFeedback) && (
          <FeedbackWarningBanner
            className="tw-mt-4"
            type={
              warningUpdateSettings
                ? FeedbackWarningType.UPDATE_SETTINGS
                : FeedbackWarningType.RESEND_FEEDBACK
            }
            onSettings={() => {
              editFeedbackSettingsModal.open();
            }}
            onResendSuccess={() => {
              setWarningResendFeedback(false);
            }}
          />
        )}
      </div>

      {isEmptyMultiSession ? (
        <EmptyMultiSessionEventFeedback />
      ) : (
        <>
          {questions.length === 0 && disabled && <PastEventEmptyFeedback />}
          {questions.length === 0 && !disabled && (
            <EventEditorEmptyQuestions
              disabled={disabled}
              onStartFromScratch={() => setIsEditQuestionSideSheetOpen(true)}
              onUseTemplate={() => setIsTemplateSelectionModalOpen(true)}
            />
          )}
          {questions.length > 0 && (
            <DraggableQuestionsList
              disabled={disabled || reorderQuestionsMutation.isPending}
              disableAddQuestion={sendAtHasPassed}
              questions={questions}
              onReorder={(reorderedQuestions) => {
                if (!event.id) return;
                reorderQuestionsMutation.mutate({
                  eventId: event.id,
                  questions: reorderedQuestions
                });
              }}
              onAddQuestion={() => setIsEditQuestionSideSheetOpen(true)}
              onEditQuestion={(question) => {
                setSelectedQuestion(question);
                setIsEditQuestionSideSheetOpen(true);
              }}
            />
          )}
        </>
      )}

      <TemplateSelectionModal
        open={isTemplateSelectionModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsTemplateSelectionModalOpen(false);
          }
        }}
        templateType={SurveyTemplateTypes.FEEDBACK}
      />
      <FeedbackPreviewModal
        open={isQuestionPreviewModalOpen}
        onOpenChange={() => setIsQuestionPreviewModalOpen(false)}
        questions={questions}
      />
      <EditFeedbackQuestionSideSheet
        open={isEditQuestionSideSheetOpen}
        onOpenChange={(open) => {
          setIsEditQuestionSideSheetOpen(open);
          if (!open) {
            setTimeout(() => {
              setSelectedQuestion(undefined);
            }, 300);
          }
        }}
        question={selectedQuestion}
        onWarningResendFeedback={() => setWarningResendFeedback(true)}
      />
    </div>
  );
};
